import { TreeNode } from "@/types/tree";
import { PostAssets } from "@/types/assets";
import {
  addOrUpdateUniqueStyleTagBeforeSpecificElementInHead,
  addUniqueLinkRelStylesheetTagBeforeSpecificElementInHead
} from "@/lib/canvas/assets/dom-utils";
import { prefixBuilderAddedCssTagId } from "@/lib/canvas/assets/css-in-head/utils";
import slugify from "slugify";
import { getDocumentHead } from "@/util/dom";
import { BuilderElement } from "@/types/elements";

export function getHtmlTagIdPrefixForElementStylesCameFromSsr(
  treeNodeId: TreeNode["id"]
): string {
  return `from_ssr__element-id__${treeNodeId}`;
}

export function removeElementStylesCameFromSsrFromCanvasHead(
  canvasDocument: Document,
  treeNodeId: TreeNode["id"]
) {
  getDocumentHead(canvasDocument)
    .querySelectorAll(
      `[id^=${prefixBuilderAddedCssTagId(
        getHtmlTagIdPrefixForElementStylesCameFromSsr(treeNodeId)
      )}]`
    )
    .forEach(element => element.remove());
}

export function addOrUpdateElementSubStylesComingFromSsr(
  canvasDocument: Document,
  treeNodeId: TreeNode["id"],
  postsGeneratedCssFilePathsFromSsr?: PostAssets["postsGeneratedCssFilePaths"]
) {
  removeElementStylesCameFromSsrFromCanvasHead(canvasDocument, treeNodeId);

  if (!postsGeneratedCssFilePathsFromSsr) {
    return;
  }

  for (const [postId, generatedCssFilePathsForSinglePost] of Object.entries(
    postsGeneratedCssFilePathsFromSsr
  )) {
    if (generatedCssFilePathsForSinglePost.postDefaultsCssFilePath) {
      addUniqueLinkRelStylesheetTagBeforeSpecificElementInHead(
        canvasDocument,
        prefixBuilderAddedCssTagId(
          `${getHtmlTagIdPrefixForElementStylesCameFromSsr(
            treeNodeId
          )}__inner_post_defaults_css_file_path__inner_post-id__${postId}`
        ),
        generatedCssFilePathsForSinglePost.postDefaultsCssFilePath,
        'meta[name="breakdance:header-assets:css:elements-defaults"][content="end"]'
      );
    }

    if (generatedCssFilePathsForSinglePost.postCssFilePath) {
      addUniqueLinkRelStylesheetTagBeforeSpecificElementInHead(
        canvasDocument,
        prefixBuilderAddedCssTagId(
          `${getHtmlTagIdPrefixForElementStylesCameFromSsr(
            treeNodeId
          )}__inner_post_css_file_path__inner_post-id__${postId}`
        ),
        generatedCssFilePathsForSinglePost.postCssFilePath,
        'meta[name="breakdance:header-assets:css:all-rendered-posts"][content="end"]'
      );
    }
  }
}

export function addOrUpdateGeneratedBuilderElementDefaultStylesInlineCSSInCanvasHead(
  canvasDocument: Document,
  builderElementSlug: string,
  rawCssCode: string
): void {
  addOrUpdateUniqueStyleTagBeforeSpecificElementInHead(
    canvasDocument,
    prefixBuilderAddedCssTagId(
      `default-styles__${slugify(builderElementSlug)}`
    ),
    rawCssCode,
    'meta[name="breakdance:header-assets:css:elements-defaults"][content="end"]'
  );
}

export function addOrUpdateGeneratedBuilderElementStylesInlineCSSInCanvasHead(
  canvasDocument: Document,
  elementId: TreeNode["id"],
  rawCssCode: string,
  suffix?: string
): void {
  addOrUpdateUniqueStyleTagBeforeSpecificElementInHead(
    canvasDocument,
    prefixBuilderAddedCssTagId(`element-styles__${elementId}_${suffix}`),
    rawCssCode,
    'meta[name="breakdance:header-assets:css:all-rendered-posts"][content="end"]'
  );
}

export function addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead(
  canvasDocument: Document,
  tagId: string,
  rawCssCode: string
): void {
  addOrUpdateUniqueStyleTagBeforeSpecificElementInHead(
    canvasDocument,
    prefixBuilderAddedCssTagId(`element-inline-dependencies__${tagId}`),
    rawCssCode,
    'meta[name="breakdance:header-assets:css:elements-dependencies-inline"][content="end"]'
  );
}

export function addOrUpdateGeneratedElementDependenciesCSSInCanvasHead(
  canvasDocument: Document,
  tagId: string,
  stylesheetUrl: string
): void {
  addUniqueLinkRelStylesheetTagBeforeSpecificElementInHead(
    canvasDocument,
    prefixBuilderAddedCssTagId(`element-dependencies__${tagId}`),
    stylesheetUrl,
    'meta[name="breakdance:header-assets:css:elements-dependencies"][content="end"]'
  );
}

export function removeElementDefaultStylesFromCanvasHead(
  canvasDocument: Document,
  elementSlug: BuilderElement["slug"]
) {
  const element = getDocumentHead(canvasDocument).querySelector(
    `#${prefixBuilderAddedCssTagId(`default-styles__${slugify(elementSlug)}`)}`
  );

  if (element) {
    element.remove();
  }
}

export function removeElementStylesFromCanvasHead(
  canvasDocument: Document,
  treeNodeId: TreeNode["id"],
  suffix?: string
) {
  const element = getDocumentHead(canvasDocument).querySelector(
    `#${prefixBuilderAddedCssTagId(`element-styles__${treeNodeId}_${suffix}`)}`
  );

  if (element) {
    element.remove();
  }
}
